import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Button, Input, DatePicker, Select, Typography, Alert, Row, Col, Statistic, Tooltip, message } from 'antd';
import { SafetyOutlined, ReloadOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import devToolsService, { LoginAttempt, LoginAttemptsResponse, ActiveLoginBlock } from '../services/devToolsService.ts';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmtDate = (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm:ss') : '-');

const OUTCOME_TAG: Record<string, { color: string; label: string }> = {
  success: { color: 'green', label: 'Riuscito' },
  failed: { color: 'orange', label: 'Fallito' },
  blocked: { color: 'red', label: 'Bloccato' },
};

const REASON_LABEL: Record<string, string> = {
  USER_IP: 'Utente da questo IP',
  IP: 'IP (più utenti)',
  USER: 'Utente (da più IP)',
};

/**
 * Strumenti → Tentativi di Accesso (solo amministratore, v3.14.0).
 * Registro dei login (riusciti, falliti, rifiutati perché bloccati) e blocchi
 * brute force attivi. Sola lettura: i blocchi scadono da soli.
 */
const LoginAttempts: React.FC = () => {
  const [data, setData] = useState<LoginAttemptsResponse | null>(null);
  const [blocks, setBlocks] = useState<ActiveLoginBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>([dayjs().subtract(30, 'day'), dayjs()]);
  const [username, setUsername] = useState('');
  const [ip, setIp] = useState('');
  const [outcomes, setOutcomes] = useState<string[]>(['failed', 'blocked']);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const load = useCallback(async (p = page, size = pageSize) => {
    setLoading(true);
    try {
      const [list, active] = await Promise.all([
        devToolsService.getLoginAttempts({
          date_from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
          date_to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
          username: username.trim() || undefined,
          ip_address: ip.trim() || undefined,
          outcome: outcomes.length ? outcomes.join(',') : undefined,
          limit: size,
          offset: (p - 1) * size,
        }),
        devToolsService.getActiveLoginBlocks(),
      ]);
      setData(list);
      setBlocks(active.blocks);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nella lettura dei tentativi di accesso');
    } finally {
      setLoading(false);
    }
  }, [range, username, ip, outcomes, page, pageSize]);

  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearch = () => { setPage(1); load(1, pageSize); };
  const handleClear = () => {
    setRange([dayjs().subtract(30, 'day'), dayjs()]);
    setUsername('');
    setIp('');
    setOutcomes(['failed', 'blocked']);
    setPage(1);
  };

  const columns = [
    { title: 'Data e ora', dataIndex: 'attempted_at', key: 'attempted_at', width: 170, render: fmtDate },
    {
      title: 'Utente',
      key: 'username',
      width: 200,
      render: (_: any, r: LoginAttempt) => (
        <Space>
          <Text strong>{r.username || '(vuoto)'}</Text>
          {!r.user_exists && <Tooltip title="Nome utente che non esiste in NAVI"><Tag>inesistente</Tag></Tooltip>}
        </Space>
      ),
    },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip_address', width: 150, render: (v: string | null) => v || '-' },
    {
      title: 'Esito',
      key: 'outcome',
      width: 220,
      render: (_: any, r: LoginAttempt) => (
        <Space>
          <Tag color={OUTCOME_TAG[r.outcome]?.color}>{OUTCOME_TAG[r.outcome]?.label || r.outcome}</Tag>
          {r.block_reason && <Text type="secondary" style={{ fontSize: 12 }}>{REASON_LABEL[r.block_reason] || r.block_reason}</Text>}
        </Space>
      ),
    },
    {
      title: 'Browser / client',
      dataIndex: 'user_agent',
      key: 'user_agent',
      ellipsis: true,
      render: (v: string | null) => v ? <Tooltip title={v}><span>{v}</span></Tooltip> : '-',
    },
  ];

  const rules = data?.rules;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}><SafetyOutlined /> Tentativi di Accesso</Title>
      {rules && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Protezione contro i tentativi ripetuti di indovinare la password"
          description={
            <div>
              Il login viene bloccato per {rules.window_minutes} minuti quando, negli ultimi {rules.window_minutes} minuti, ci sono:
              {' '}<b>{rules.user_ip_max_failures}</b> tentativi falliti dello stesso utente dallo stesso IP,
              {' '}<b>{rules.ip_max_failures}</b> dallo stesso IP su utenti qualsiasi,
              {' '}<b>{rules.user_max_failures}</b> sullo stesso utente da IP diversi.
              Un accesso riuscito azzera i conteggi per utente. I blocchi scadono da soli.
            </div>
          }
        />
      )}

      <Card
        size="small"
        title={`Blocchi attivi adesso (${blocks.length})`}
        style={{ marginBottom: 16 }}
        extra={<Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Aggiorna</Button>}
      >
        {blocks.length === 0 ? (
          <Text type="secondary">Nessun blocco attivo.</Text>
        ) : (
          <Table
            size="small"
            rowKey={(b) => `${b.reason}-${b.username}-${b.ip_address}`}
            pagination={false}
            dataSource={blocks}
            columns={[
              { title: 'Tipo', dataIndex: 'reason', key: 'reason', render: (v: string) => <Tag color="red">{REASON_LABEL[v] || v}</Tag> },
              { title: 'Utente', dataIndex: 'username', key: 'username', render: (v: string | null) => v || 'tutti' },
              { title: 'IP', dataIndex: 'ip_address', key: 'ip_address', render: (v: string | null) => v || 'tutti' },
              { title: 'Bloccato fino a', dataIndex: 'until', key: 'until', render: fmtDate },
            ]}
          />
        )}
      </Card>

      {data && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}><Card size="small"><Statistic title="Riusciti" value={data.summary.success} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="Falliti" value={data.summary.failed} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="Bloccati" value={data.summary.blocked} valueStyle={{ color: '#f5222d' }} /></Card></Col>
          <Col span={12}>
            <Card size="small" title="IP con più tentativi falliti o bloccati nel periodo" bodyStyle={{ padding: 8 }}>
              {data.summary.top_ips.length === 0 ? <Text type="secondary">Nessuno</Text> : (
                <Space wrap>
                  {data.summary.top_ips.map(t => (
                    <Tag key={t.ip_address || 'none'} color={t.blocked > 0 ? 'red' : 'orange'} style={{ cursor: 'pointer' }}
                      onClick={() => setIp(t.ip_address || '')}>
                      {t.ip_address || 'sconosciuto'}: {t.failed} falliti, {t.blocked} bloccati, {t.usernames} utenti
                    </Tag>
                  ))}
                </Space>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <RangePicker format="DD/MM/YYYY" value={range as any} onChange={(v) => setRange(v as any)} />
          <Input placeholder="Utente" value={username} onChange={e => setUsername(e.target.value)}
            onPressEnter={handleSearch} style={{ width: 180 }} className="no-uppercase-modal" />
          <Input placeholder="IP" value={ip} onChange={e => setIp(e.target.value)} onPressEnter={handleSearch} style={{ width: 160 }} />
          <Select
            mode="multiple"
            placeholder="Esito"
            value={outcomes}
            onChange={setOutcomes}
            style={{ minWidth: 260 }}
            options={[
              { value: 'failed', label: 'Fallito' },
              { value: 'blocked', label: 'Bloccato' },
              { value: 'success', label: 'Riuscito' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Filtra</Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>Pulisci</Button>
        </Space>
      </Card>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['50', '100', '200', '500'],
          showTotal: (total) => `${total} tentativi`,
          onChange: (p, size) => { setPage(p); setPageSize(size); load(p, size); },
        }}
      />
    </div>
  );
};

export default LoginAttempts;
